// ADMIN-5 members table: per-member usage for the month with search, filters, sortable
// columns and paging. All of it runs on the list the usage endpoint already returned
// (lib/memberTable.ts); budget edits still go through the parent's mutation.
import { useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import type { BudgetState, MemberUsage } from '@scribe/core/types';
import { Badge, IconButton, Pagination, SearchInput, formatUsd } from './ui';
import { formatGb, formatMinutes, formatTokens } from '../lib/adminFormat';
import {
  DEFAULT_MEMBER_TABLE_QUERY,
  PAGE_SIZES,
  applyMemberTableQuery,
  type ActivityFilter,
  type BudgetStateFilter,
  type MemberSortKey,
  type MemberTableQuery,
} from '../lib/memberTable';

const BUDGET_BADGE_KIND: Record<BudgetState, string> = {
  ok: 'success',
  warn: 'pending',
  exceeded: 'danger',
};

const BUDGET_STATE_LABEL: Record<BudgetState, string> = {
  ok: 'OK',
  warn: 'Warning',
  exceeded: 'Exceeded',
};

export function BudgetStateBadge({ state }: { state: BudgetState }) {
  return <Badge kind={BUDGET_BADGE_KIND[state]}>{BUDGET_STATE_LABEL[state]}</Badge>;
}

/** Column order and labels. Text columns sort A→Z first; number columns biggest first,
 * since "who uses the most" is the usual question. */
const COLUMNS: { key: MemberSortKey; label: string; numeric: boolean }[] = [
  { key: 'member', label: 'Member', numeric: false },
  { key: 'recordings', label: 'Recordings', numeric: true },
  { key: 'batch', label: 'Batch', numeric: true },
  { key: 'streaming', label: 'Streaming', numeric: true },
  { key: 'storage', label: 'Storage', numeric: true },
  { key: 'tokens', label: 'AI tokens', numeric: true },
  { key: 'cost', label: 'Est. cost', numeric: true },
  { key: 'budget', label: 'Budget', numeric: true },
  { key: 'state', label: 'State', numeric: true },
];

export function MembersTable({
  members,
  saving,
  onSaveBudget,
}: {
  members: readonly MemberUsage[];
  saving: boolean;
  onSaveBudget: (userId: string, monthlyBudgetUsd: number | null) => void;
}) {
  const [query, setQuery] = useState<MemberTableQuery>(DEFAULT_MEMBER_TABLE_QUERY);
  const { items, pageInfo, matched } = useMemo(
    () => applyMemberTableQuery(members, query),
    [members, query],
  );
  const filtering = query.search.trim() !== '' || query.state !== 'all' || query.activity !== 'all';

  // Any change other than paging starts again at page 1.
  function update(patch: Partial<MemberTableQuery>) {
    setQuery((q) => ({ ...q, page: 1, ...patch }));
  }

  function sortBy(key: MemberSortKey, numeric: boolean) {
    setQuery((q) =>
      q.sortKey === key
        ? { ...q, page: 1, sortDirection: q.sortDirection === 'asc' ? 'desc' : 'asc' }
        : { ...q, page: 1, sortKey: key, sortDirection: numeric ? 'desc' : 'asc' },
    );
  }

  function clearFilters() {
    update({ search: '', state: 'all', activity: 'all' });
  }

  return (
    <div className="admin-members">
      <div className="toolbar">
        <SearchInput
          value={query.search}
          onChange={(search) => update({ search })}
          placeholder="Search members by name or email"
          ariaLabel="Search members"
        />
        <select
          aria-label="Filter by budget state"
          value={query.state}
          onChange={(e) => update({ state: e.target.value as BudgetStateFilter })}
        >
          <option value="all">All budget states</option>
          <option value="ok">OK</option>
          <option value="warn">Warning</option>
          <option value="exceeded">Exceeded</option>
        </select>
        <select
          aria-label="Filter by activity"
          value={query.activity}
          onChange={(e) => update({ activity: e.target.value as ActivityFilter })}
        >
          <option value="all">All members</option>
          <option value="active">Used Scribe this month</option>
          <option value="inactive">No use this month</option>
        </select>
        {filtering && (
          <button type="button" className="ghost sm" onClick={clearFilters}>
            Clear filters
          </button>
        )}
        <span className="count" aria-live="polite">
          {filtering ? `${matched} of ${members.length} members` : `${members.length} members`}
        </span>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {COLUMNS.map((c) => {
                const active = query.sortKey === c.key;
                const Icon = !active
                  ? ChevronUpDownIcon
                  : query.sortDirection === 'asc'
                    ? ChevronUpIcon
                    : ChevronDownIcon;
                return (
                  <th
                    key={c.key}
                    aria-sort={active ? (query.sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      className={'th-sort' + (active ? ' on' : '')}
                      onClick={() => sortBy(c.key, c.numeric)}
                    >
                      {c.label}
                      <Icon width={14} height={14} aria-hidden="true" />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr>
                <td className="empty" colSpan={COLUMNS.length}>
                  No usage recorded for this month.
                </td>
              </tr>
            )}
            {members.length > 0 && matched === 0 && (
              <tr>
                <td className="empty" colSpan={COLUMNS.length}>
                  No members match these filters.{' '}
                  <button type="button" className="ghost sm" onClick={clearFilters}>
                    Clear filters
                  </button>
                </td>
              </tr>
            )}
            {items.map((m) => (
              <MemberRow
                key={m.user_id}
                member={m}
                saving={saving}
                onSave={(monthlyBudgetUsd) => onSaveBudget(m.user_id, monthlyBudgetUsd)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {matched > PAGE_SIZES[0] && (
        <div className="admin-members-pager">
          <Pagination pageInfo={pageInfo} onPageChange={(page) => setQuery((q) => ({ ...q, page }))} />
          <label className="pagination-page">
            Rows per page{' '}
            <select
              value={query.pageSize}
              onChange={(e) => update({ pageSize: Number(e.target.value) })}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function MemberRow({
  member,
  saving,
  onSave,
}: {
  member: MemberUsage;
  saving: boolean;
  onSave: (monthlyBudgetUsd: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const effectiveBudget = member.monthly_budget_usd ?? null;

  function startEdit() {
    setDraft(effectiveBudget != null ? String(effectiveBudget) : '');
    setEditing(true);
  }

  function save() {
    const trimmed = draft.trim();
    onSave(trimmed === '' ? null : Number(trimmed));
    setEditing(false);
  }

  return (
    <tr>
      <td>
        <div>{member.display_name}</div>
        <div className="muted">{member.email}</div>
      </td>
      <td>{member.recordings_count.toLocaleString()}</td>
      <td>{formatMinutes(member.batch_seconds)}</td>
      <td>{formatMinutes(member.streaming_seconds)}</td>
      <td>{formatGb(member.storage_bytes)}</td>
      <td>{formatTokens(member.ai_tokens_in, member.ai_tokens_out)}</td>
      <td>{formatUsd(member.estimated_cost_usd)}</td>
      <td>
        {editing ? (
          <span className="row admin-budget-edit">
            <input
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              aria-label={`Monthly budget for ${member.display_name}`}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') setEditing(false);
              }}
            />
            <button className="sm" disabled={saving} onClick={save}>
              Save
            </button>
            <button className="ghost sm" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <span className="row admin-budget-display">
            {effectiveBudget != null ? formatUsd(effectiveBudget) : '—'}
            <IconButton label={`Edit budget for ${member.display_name}`} onClick={startEdit}>
              <PencilSquareIcon width={16} height={16} aria-hidden="true" />
            </IconButton>
          </span>
        )}
      </td>
      <td>
        <BudgetStateBadge state={member.budget_state} />
      </td>
    </tr>
  );
}
